import type { ModerationMetricsDto } from '@planuze/admin-contracts';
import { cn, Skeleton, Stat, StatGrid } from '@planuze/ui';
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  Clock,
  Gauge,
  ListChecks,
  Timer,
  Zap,
} from 'lucide-react';
import { useMetricsWidgetsContent } from '../../../content/sections/metrics-widgets.js';
import { fmtNumber } from '../../../lib/format.js';

interface Props {
  readonly metrics: ModerationMetricsDto | undefined;
}

const SKELETON_COUNT = 8;

/* Aba Moderação: expõe o `moderationMetricsDto` por inteiro em cards de SLA (Stat/StatGrid),
   não só queueDepth + aboveSlaCount. `slaTargetHours` ancora o p95 (delta colorido por
   tendência) e o card de pendências acima do alvo (borda destrutiva quando > 0). */
export function ModerationTab({ metrics }: Props) {
  const copy = useMetricsWidgetsContent().moderationTab;
  if (metrics === undefined) {
    return (
      <StatGrid columns={4}>
        {Array.from({ length: SKELETON_COUNT }, (_, i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </StatGrid>
    );
  }
  const m = metrics;
  const p95Breached = m.timeToDecisionP95Hours > m.slaTargetHours;
  const slaDelta = m.timeToDecisionP95Hours - m.slaTargetHours;
  return (
    <StatGrid columns={4}>
      <Stat label={copy.queueDepth} value={fmtNumber(m.queueDepth)} icon={<Clock />} />
      <Stat
        label={copy.decisionP50}
        value={`${m.timeToDecisionP50Hours}h`}
        hint={copy.decisionP50Hint}
        icon={<Timer />}
      />
      <Stat
        label={copy.decisionP95}
        value={`${m.timeToDecisionP95Hours}h`}
        hint={copy.decisionP95Hint(m.slaTargetHours)}
        change={`${slaDelta > 0 ? '+' : ''}${slaDelta}h`}
        trend={p95Breached ? 'down' : 'up'}
        icon={<Gauge />}
      />
      <Stat
        label={copy.aboveSla(m.slaTargetHours)}
        value={fmtNumber(m.aboveSlaCount)}
        hint={copy.aboveSlaHint}
        icon={<AlertTriangle />}
        className={cn(m.aboveSlaCount > 0 && 'border-destructive/40')}
      />
      <Stat label={copy.approved} value={fmtNumber(m.approvedCount)} icon={<CheckCircle2 />} />
      <Stat label={copy.rejected} value={fmtNumber(m.rejectedCount)} icon={<Ban />} />
      <Stat label={copy.fastpath} value={fmtNumber(m.fastpathCount)} icon={<Zap />} />
      <Stat
        label={copy.autoRejectCap}
        value={fmtNumber(m.autoRejectByCapCount)}
        icon={<ListChecks />}
      />
    </StatGrid>
  );
}
